import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { Alert } from 'react-daisyui';
import Routes from '@/router/Routes.ts';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import SelectInput from '@/components/functional/SelectInput/SelectInput.tsx';
import SelectionChip from '@/components/functional/SelectionChip/SelectionChip.tsx';
import SelectionRadio from '@/components/functional/SelectionRadio/SelectionRadio.tsx';
import StepFormInput from '@/components/functional/StepFormInput/StepFormInput.tsx';
import StepProgressBar from '@/components/ui/StepProgressBar/StepProgressBar.tsx';
import StepFormActions from '@/components/shared/StepFormActions/StepFormActions.tsx';
import StepFormSubtitle from '@/components/ui/StepFormSubtitle/StepFormSubtitle.tsx';
import StepFormTitle from '@/components/ui/StepFormTitle/StepFormTitle.tsx';
import Stepper from '@/components/shared/Stepper/Stepper.tsx';
import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import useSaveContext from '@/api/useSaveContext.ts';
import useFrameworkPreview from '@/api/useFrameworkPreview.ts';
import CommandSignalChip from '@/components/ui/CommandSignalChip/CommandSignalChip.tsx';
import { FRAMEWORK_BY_ID, confidenceLevel } from '@/constants/frameworks.ts';
import { useDebounce } from '@/hooks/useDebounce.ts';

const steps = [
  'intro',
  'brand-role',
  'business-profile',
  'sector-context',
  'strategic-orientation',
  'operating-metadata',
  'review',
] as const;
const ONBOARDING_PROGRESS_STEPS = 5;

const BRAND_ROLE_OPTIONS = ['CEO', 'COO', 'CTO', 'CFO', 'CMO', 'CPO', 'VP / Director', 'Other'];

const STRATEGIC_ORIENTATION_OPTIONS = [
  'growth',
  'turnaround',
  'margin improvement',
  'expansion',
  'innovation',
  'restructuring',
  'capital readiness',
];

const COMPANY_SIZE_OPTIONS = [
  { value: 'small', label: 'Small (1–100 employees)' },
  { value: 'medium', label: 'Medium (101–999 employees)' },
  { value: 'large', label: 'Large (1,000+ employees)' },
];

const ORGANIZATIONAL_LIFECYCLE_OPTIONS = [
  { value: 'formation', label: 'Formation' },
  { value: 'expansion', label: 'Expansion' },
  { value: 'merger_acquisition', label: 'Merger / Acquisition' },
  { value: 'renewal_transformation', label: 'Renewal / Transformation' },
];

const VALUE_CREATION_SYSTEM_OPTIONS = [
  { value: 'service_based', label: 'Service-Based' },
  { value: 'asset_based', label: 'Asset-Based' },
  { value: 'membership_based', label: 'Membership-Based' },
  { value: 'product_based', label: 'Product-Based' },
  { value: 'transaction_based', label: 'Transaction-Based' },
  { value: 'platform_based', label: 'Platform-Based' },
  { value: 'hybrid', label: 'Hybrid' },
];

const KPI_OPTIONS = [
  'Workforce Capacity',
  'Revenue Realization',
  'Conversion Velocity',
  'Client Retention',
  'Customer Acquisition Efficiency',
  'Resource Utilization',
  'Demand Generation',
  'Capital Position',
  'Portfolio Load',
];

type OnboardingData = {
  brand_role: string;
  company_size: string;
  team_structure: string;
  stage: string;
  geography: string;
  revenue_range: string;
  operating_model: string;
  industry: string;
  market_dynamics: string;
  regulatory_environment: string;
  customer_type: string;
  competitive_landscape: string;
  strategic_orientation: string;
  operating_kpis: string[];
  operating_other: string;
};

const OnboardingPage: FC = () => (
  <Stepper.Wrapper steps={steps} initialStep="intro">
    <Stepper.Step<typeof steps> step="intro">
      <StepIntro />
    </Stepper.Step>
    <Stepper.Step<typeof steps> step="brand-role">
      <StepBrandRole />
    </Stepper.Step>
    <Stepper.Step<typeof steps> step="business-profile">
      <StepBusinessProfile />
    </Stepper.Step>
    <Stepper.Step<typeof steps> step="sector-context">
      <StepSectorContext />
    </Stepper.Step>
    <Stepper.Step<typeof steps> step="strategic-orientation">
      <StepStrategicOrientation />
    </Stepper.Step>
    <Stepper.Step<typeof steps> step="operating-metadata">
      <StepOperatingMetadata />
    </Stepper.Step>
    <Stepper.Step<typeof steps> step="review">
      <StepReview />
    </Stepper.Step>
  </Stepper.Wrapper>
);

export default OnboardingPage;

function StepIntro() {
  const { t } = useTranslation();
  const { nextStep } = useStepperContext<typeof steps, OnboardingData>();

  return (
    <BrandCardSideContent centerContent>
      <div className="flex flex-col gap-4 w-full">
        <div className="flex flex-col gap-2">
          <StepFormTitle>{t('onboardingPage.intro.title')}</StepFormTitle>
          <StepFormSubtitle>{t('onboardingPage.intro.subtitle')}</StepFormSubtitle>
        </div>
        <LoadingButton loading={false} color="primary" className="w-full" onClick={nextStep}>
          {t('onboardingPage.intro.button')}
        </LoadingButton>
      </div>
    </BrandCardSideContent>
  );
}

function StepBrandRole() {
  const { t } = useTranslation();
  const { nextStep, previousStep, setContextData, contextData } = useStepperContext<typeof steps, OnboardingData>();
  const d = contextData as Partial<OnboardingData> | null;

  const { handleSubmit, watch, setValue } = useForm<{ brand_role: string }>({
    defaultValues: { brand_role: d?.brand_role ?? '' },
  });

  const selected = watch('brand_role');

  const onSubmit = ({ brand_role }: { brand_role: string }) => {
    setContextData((prev) => ({ ...prev, brand_role }));
    nextStep();
  };

  return (
    <BrandCardSideContent>
      <form className="flex flex-col flex-1 gap-6 w-full" onSubmit={handleSubmit(onSubmit)}>
        <StepProgressBar totalSteps={ONBOARDING_PROGRESS_STEPS} current={1} />
        <StepFormTitle>{t('onboardingPage.brandRole.title')}</StepFormTitle>
        <div className="flex flex-col gap-2">
          {BRAND_ROLE_OPTIONS.map((opt) => (
            <SelectionRadio
              key={opt}
              label={opt}
              selected={selected === opt}
              onClick={() => {
                setValue('brand_role', opt);
                setContextData((prev) => ({ ...prev, brand_role: opt }));
              }}
            />
          ))}
        </div>
        <StepFormActions onBack={previousStep} continueDisabled={!selected} />
      </form>
    </BrandCardSideContent>
  );
}

type BusinessProfileFields = {
  company_size: string;
  team_structure: string;
  stage: string;
  geography: string;
  revenue_range: string;
  operating_model: string;
};

function StepBusinessProfile() {
  const { t } = useTranslation();
  const { nextStep, previousStep, setContextData, contextData } = useStepperContext<typeof steps, OnboardingData>();
  const d = contextData as Partial<OnboardingData> | null;

  const {
    register,
    handleSubmit,
    control,
    formState: { isValid: isBusinessValid },
  } = useForm<BusinessProfileFields>({
    mode: 'onChange',
    defaultValues: {
      company_size: d?.company_size ?? '',
      team_structure: d?.team_structure ?? '',
      stage: d?.stage ?? '',
      geography: d?.geography ?? '',
      revenue_range: d?.revenue_range ?? '',
      operating_model: d?.operating_model ?? '',
    },
  });

  const onSubmit = (data: BusinessProfileFields) => {
    setContextData((prev) => ({ ...prev, ...data }));
    nextStep();
  };

  return (
    <BrandCardSideContent>
      <form className="flex flex-col flex-1 gap-6 w-full" onSubmit={handleSubmit(onSubmit)}>
        <StepProgressBar totalSteps={ONBOARDING_PROGRESS_STEPS} current={2} />
        <StepFormTitle>{t('onboardingPage.businessProfile.title')}</StepFormTitle>
        <div className="grid grid-cols-2 gap-3">
          {([
            ['company_size', t('onboardingPage.businessProfile.companySize'), COMPANY_SIZE_OPTIONS],
            ['stage', t('onboardingPage.businessProfile.stage'), ORGANIZATIONAL_LIFECYCLE_OPTIONS],
            ['operating_model', t('onboardingPage.businessProfile.operatingModel'), VALUE_CREATION_SYSTEM_OPTIONS],
          ] as const).map(([key, placeholder, options]) => (
            <Controller
              key={key}
              name={key}
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <SelectInput
                  value={field.value}
                  onChange={field.onChange}
                  options={options as { value: string; label: string }[]}
                  bordered={false}
                  placeholder={placeholder}
                  labelClassName="!border !border-[rgba(0,0,0,0.12)] !bg-white !rounded-xl !h-auto !min-h-0 !py-3.5 !px-4 font-dm text-sm text-black"
                  wrapperClassName="w-full"
                />
              )}
            />
          ))}
          {(
            [
              ['team_structure', t('onboardingPage.businessProfile.teamStructure')],
              ['geography', t('onboardingPage.businessProfile.geography')],
              ['revenue_range', t('onboardingPage.businessProfile.revenueRange')],
            ] as const
          ).map(([key, placeholder]) => (
            <StepFormInput key={key} placeholder={placeholder} {...register(key, { required: true })} />
          ))}
        </div>
        <StepFormActions onBack={previousStep} continueDisabled={!isBusinessValid} />
      </form>
    </BrandCardSideContent>
  );
}

type SectorContextFields = {
  industry: string;
  market_dynamics: string;
  regulatory_environment: string;
  customer_type: string;
  competitive_landscape: string;
};

function StepSectorContext() {
  const { t } = useTranslation();
  const { nextStep, previousStep, setContextData, contextData } = useStepperContext<typeof steps, OnboardingData>();
  const d = contextData as Partial<OnboardingData> | null;

  const {
    register,
    handleSubmit,
    watch,
    formState: { isValid: isSectorValid },
  } = useForm<SectorContextFields>({
    mode: 'onChange',
    defaultValues: {
      industry: d?.industry ?? '',
      market_dynamics: d?.market_dynamics ?? '',
      regulatory_environment: d?.regulatory_environment ?? '',
      customer_type: d?.customer_type ?? '',
      competitive_landscape: d?.competitive_landscape ?? '',
    },
  });

  const industry = watch('industry');
  const debouncedIndustry = useDebounce(industry, 500);
  const { data: sectorPreview, isLoading: isSectorPreviewLoading } = useFrameworkPreview(
    debouncedIndustry,
    d?.strategic_orientation ?? '',
    '',
    !!debouncedIndustry
  );

  const onSubmit = (data: SectorContextFields) => {
    setContextData((prev) => ({ ...prev, ...data }));
    nextStep();
  };

  return (
    <BrandCardSideContent>
      <form className="flex flex-col flex-1 gap-6 w-full" onSubmit={handleSubmit(onSubmit)}>
        <StepProgressBar totalSteps={ONBOARDING_PROGRESS_STEPS} current={3} />
        {(isSectorPreviewLoading || sectorPreview?.primary_framework) && (
          <CommandSignalChip
            frameworkId={sectorPreview?.primary_framework}
            confidence={sectorPreview?.routing_confidence}
            isLoading={isSectorPreviewLoading}
          />
        )}
        <StepFormTitle>{t('onboardingPage.sectorContext.title')}</StepFormTitle>
        <StepFormSubtitle>{t('onboardingPage.sectorContext.subtitle')}</StepFormSubtitle>
        <div className="grid grid-cols-2 gap-3">
          <StepFormInput
            placeholder={t('onboardingPage.sectorContext.industry')}
            {...register('industry', { required: true })}
          />
          <StepFormInput
            placeholder={t('onboardingPage.sectorContext.marketDynamics')}
            {...register('market_dynamics', { required: true })}
          />
          <StepFormInput
            placeholder={t('onboardingPage.sectorContext.regulatoryEnvironment')}
            {...register('regulatory_environment', { required: true })}
          />
          <StepFormInput
            placeholder={t('onboardingPage.sectorContext.customerType')}
            {...register('customer_type', { required: true })}
          />
          <StepFormInput
            className="col-span-2"
            placeholder={t('onboardingPage.sectorContext.competitiveLandscape')}
            {...register('competitive_landscape', { required: true })}
          />
        </div>
        <StepFormActions onBack={previousStep} continueDisabled={!isSectorValid} />
      </form>
    </BrandCardSideContent>
  );
}

function StepStrategicOrientation() {
  const { t } = useTranslation();
  const { nextStep, previousStep, setContextData, contextData } = useStepperContext<typeof steps, OnboardingData>();
  const d = contextData as Partial<OnboardingData> | null;

  const { handleSubmit, watch, setValue } = useForm<{ strategic_orientation: string }>({
    defaultValues: { strategic_orientation: d?.strategic_orientation ?? '' },
  });

  const selected = watch('strategic_orientation');
  const debouncedOrientation = useDebounce(selected, 200);
  const { data: orientationPreview, isLoading: isOrientationPreviewLoading } = useFrameworkPreview(
    d?.industry ?? '',
    debouncedOrientation,
    '',
    !!(d?.industry || debouncedOrientation)
  );

  const onSubmit = ({ strategic_orientation }: { strategic_orientation: string }) => {
    setContextData((prev) => ({ ...prev, strategic_orientation }));
    nextStep();
  };

  return (
    <BrandCardSideContent>
      <form className="flex flex-col flex-1 gap-6 w-full" onSubmit={handleSubmit(onSubmit)}>
        <StepProgressBar totalSteps={ONBOARDING_PROGRESS_STEPS} current={4} />
        {(isOrientationPreviewLoading || orientationPreview?.primary_framework) && (
          <CommandSignalChip
            frameworkId={orientationPreview?.primary_framework}
            confidence={orientationPreview?.routing_confidence}
            isLoading={isOrientationPreviewLoading}
          />
        )}
        <StepFormTitle>{t('onboardingPage.strategicOrientation.title')}</StepFormTitle>
        <div className="flex flex-col gap-2">
          {STRATEGIC_ORIENTATION_OPTIONS.map((opt) => (
            <SelectionRadio
              key={opt}
              label={opt}
              selected={selected === opt}
              onClick={() => {
                setValue('strategic_orientation', opt);
                setContextData((prev) => ({ ...prev, strategic_orientation: opt }));
              }}
            />
          ))}
        </div>
        <StepFormActions onBack={previousStep} continueDisabled={!selected} />
      </form>
    </BrandCardSideContent>
  );
}

type OperatingMetadataFields = {
  operating_kpis: string[];
  operating_other: string;
};

function StepOperatingMetadata() {
  const { t } = useTranslation();
  const { nextStep, previousStep, setContextData, contextData } = useStepperContext<typeof steps, OnboardingData>();
  const d = contextData as Partial<OnboardingData> | null;
  const [showOther, setShowOther] = useState(false);

  const { data: metaPreview, isLoading: isMetaPreviewLoading } = useFrameworkPreview(
    d?.industry ?? '',
    d?.strategic_orientation ?? '',
    '',
    !!(d?.industry || d?.strategic_orientation)
  );

  const { register, handleSubmit, watch, setValue } = useForm<OperatingMetadataFields>({
    defaultValues: {
      operating_kpis: d?.operating_kpis ?? [],
      operating_other: d?.operating_other ?? '',
    },
  });

  const kpis = watch('operating_kpis');

  const toggle = (opt: string) => {
    setValue('operating_kpis', kpis.includes(opt) ? kpis.filter((x) => x !== opt) : [...kpis, opt]);
  };

  const onSubmit = (data: OperatingMetadataFields) => {
    setContextData((prev) => ({ ...prev, ...data }));
    nextStep();
  };

  return (
    <BrandCardSideContent>
      <form className="flex flex-col flex-1 gap-6 w-full" onSubmit={handleSubmit(onSubmit)}>
        <StepProgressBar totalSteps={ONBOARDING_PROGRESS_STEPS} current={5} />
        {(isMetaPreviewLoading || metaPreview?.primary_framework) && (
          <CommandSignalChip
            frameworkId={metaPreview?.primary_framework}
            confidence={metaPreview?.routing_confidence}
            isLoading={isMetaPreviewLoading}
          />
        )}
        <div>
          <StepFormTitle>{t('onboardingPage.operatingMetadata.title')}</StepFormTitle>
          <p className="font-dm text-sm text-black/40 mt-1">{t('onboardingPage.selectAll')}</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {KPI_OPTIONS.map((opt) => (
            <SelectionChip key={opt} label={opt} selected={kpis.includes(opt)} onClick={() => toggle(opt)} />
          ))}
          <SelectionChip
            label={t('onboardingPage.otherSelectedKpis')}
            selected={showOther}
            onClick={() => setShowOther((p) => !p)}
          />
        </div>
        {showOther && (
          <StepFormInput placeholder={t('onboardingPage.otherPlaceholder')} {...register('operating_other')} />
        )}
        <StepFormActions onBack={previousStep} continueDisabled={kpis.length === 0} />
      </form>
    </BrandCardSideContent>
  );
}

function StepReview() {
  const { t } = useTranslation();
  const { previousStep, contextData } = useStepperContext<typeof steps, OnboardingData>();
  const navigate = useNavigate();
  const d = contextData as Partial<OnboardingData>;

  const { mutateAsync: saveContext, isPending, isError, error } = useSaveContext();

  const { data: preview, isLoading: isLoadingPreview } = useFrameworkPreview(
    d.industry ?? '',
    d.strategic_orientation ?? '',
    '',
    !!(d.industry || d.strategic_orientation)
  );

  const frameworkLabel = preview?.primary_framework
    ? (FRAMEWORK_BY_ID[preview.primary_framework as keyof typeof FRAMEWORK_BY_ID]?.label ?? preview.primary_framework)
    : '[Framework]';
  const confidenceLabel =
    preview?.routing_confidence != null ? confidenceLevel(preview.routing_confidence) : 'Moderate';

  const onSave = () => {
    void (async () => {
      try {
        await saveContext({
          brand_role: d.brand_role ?? '',
          sector: {
            industry: d.industry ?? '',
            market_dynamics: d.market_dynamics ?? '',
            regulatory_environment: d.regulatory_environment ?? '',
            customer_type: d.customer_type ?? '',
            competitive_landscape: d.competitive_landscape ?? '',
          },
          strategic_orientation: d.strategic_orientation ?? '',
          operating_constraints: {
            kpis: d.operating_kpis ?? [],
            other: d.operating_other || undefined,
          },
          metadata: {
            company_size: d.company_size ?? '',
            team_structure: d.team_structure ?? '',
            stage: d.stage ?? '',
            geography: d.geography ?? '',
            revenue_range: d.revenue_range ?? '',
            operating_model: d.operating_model ?? '',
            industry: d.industry ?? '',
            market_dynamics: d.market_dynamics ?? '',
            regulatory_environment: d.regulatory_environment ?? '',
            customer_type: d.customer_type ?? '',
            competitive_landscape: d.competitive_landscape ?? '',
            strategic_orientation: d.strategic_orientation ?? '',
          },
        });
        navigate(Routes.DASHBOARD);
      } catch (e) {
        console.error(e);
      }
    })();
  };

  return (
    <BrandCardSideContent>
      <div className="flex flex-col flex-1 gap-6 w-full overflow-y-auto">
        <StepFormTitle>{t('onboardingPage.review.title')}</StepFormTitle>
        {isError && <Alert status="error">{error?.message}</Alert>}
        <div className="rounded-2xl bg-black/[0.04] p-5 flex flex-col gap-4 font-dm text-sm text-black/60 leading-relaxed relative">
          {isLoadingPreview && (
            <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-black/[0.04]">
              <span className="loading loading-spinner loading-md text-black/40" />
            </div>
          )}
          <p>
            <strong className="text-black font-semibold">COMMAND</strong> {t('onboardingPage.review.intro')}
          </p>
          <p>
            {t('onboardingPage.review.context1')}{' '}
            <strong className="text-black font-semibold">{d.industry || '[industry]'}</strong>{' '}
            {t('onboardingPage.review.context2')}{' '}
            <strong className="text-black font-semibold">{d.customer_type || '[customer type]'}</strong>{' '}
            {t('onboardingPage.review.context3')}{' '}
            <strong className="text-black font-semibold">{d.market_dynamics || '[market dynamics]'}</strong>{' '}
            {t('onboardingPage.review.context4')}{' '}
            <strong className="text-black font-semibold">{d.strategic_orientation || '[primary objective]'}</strong>
            {t('onboardingPage.review.context5')}{' '}
            <strong className="text-black font-semibold">
              {[d.stage, d.revenue_range].filter(Boolean).join(' · ') || '[positioning]'}
            </strong>
            .
          </p>
          <p>{t('onboardingPage.review.explanation')}</p>
          <p>
            {t('onboardingPage.review.routing1')} <strong className="text-black font-semibold">{frameworkLabel}</strong>
            {t('onboardingPage.review.routing2')}{' '}
            <strong className="text-black font-semibold">{confidenceLabel}</strong>{' '}
            {t('onboardingPage.review.routing3')}
          </p>
          <p>{t('onboardingPage.review.persistence')}</p>
        </div>
        <StepFormActions
          onBack={previousStep}
          submitType="button"
          onContinue={onSave}
          continueLabel={t('onboardingPage.review.save')}
          continueLoading={isPending}
        />
      </div>
    </BrandCardSideContent>
  );
}
